/**
 * @file StageFormFields.tsx
 * @description Поля формы стадии воронки (phase19.31 — вынесено из
 * `StageEditDialog`): код и вид, название, цвет-метка, срок стадии и флажок
 * «Активна» (только при правке).
 *
 * Контракт `value/onChange` — окно держит значения, блок только рисует.
 * Цвет — круглые кнопки с целью касания 48px; флажок — общий `Checkbox`.
 */

import { STAGE_KINDS, STAGE_PALETTE, type StageFormValue } from "./stage-form";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { SelectField } from "../../ui/SelectField";
import { STAGE_KIND_LABEL, type StageKind } from "../../../lib/api-crm";
import { cn } from "@/lib/utils";

interface Props {
  value: StageFormValue;
  onChange: (patch: Partial<StageFormValue>) => void;
  /** Правка существующей стадии — показывается флажок «Активна». */
  editing: boolean;
}

const KIND_OPTIONS = STAGE_KINDS.map((k) => ({ value: k, label: STAGE_KIND_LABEL[k] }));

export function StageFormFields({ value, onChange, editing }: Props) {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="st-code">Код</Label>
          <Input
            id="st-code"
            value={value.code}
            onChange={(e) => onChange({ code: e.target.value.toLowerCase() })}
            placeholder="qualified"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="st-kind">Вид стадии</Label>
          <SelectField
            id="st-kind"
            ariaLabel="Вид стадии"
            value={value.kind}
            onChange={(kind) => onChange({ kind: kind as StageKind })}
            options={KIND_OPTIONS}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="st-name">Название</Label>
        <Input id="st-name" value={value.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Уточнение" autoFocus />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label id="st-color-label">Цвет метки</Label>
        <div role="radiogroup" aria-labelledby="st-color-label" className="flex flex-wrap gap-1">
          {STAGE_PALETTE.map((p) => (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={value.color === p}
              aria-label={`Цвет ${p}`}
              onClick={() => onChange({ color: p })}
              className="flex size-12 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span
                className={cn("size-7 rounded-full border-2", value.color === p ? "border-on-surface" : "border-transparent")}
                style={{ backgroundColor: p }}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="st-sla">Срок стадии, часов</Label>
        <Input
          id="st-sla"
          type="number"
          inputMode="numeric"
          min={0}
          max={8760}
          value={value.slaHours}
          onChange={(e) => onChange({ slaHours: e.target.value })}
          placeholder="24"
        />
        <span className="type-caption text-on-surface-variant">
          Пусто — без срока. Ориентиры: ответ юрлицу — до 4 ч, коммерческое предложение и отгрузка — до 24 ч.
        </span>
      </div>

      {editing ? (
        <label className="type-body flex min-h-12 cursor-pointer items-center gap-2 text-on-surface">
          <Checkbox checked={value.isActive} onCheckedChange={(c) => onChange({ isActive: c === true })} />
          Активна
        </label>
      ) : null}
    </>
  );
}
